/**
 * <cart-line-subscribe> custom element
 *
 * Renders the cart-drawer per-line Subscribe & Save control.
 *
 * State is read fresh from data-* attributes on every connectedCallback —
 * the entire cart drawer re-renders on every mutation, so this element is
 * destroyed and recreated frequently. Never persist JS-side state across
 * re-renders.
 *
 * See: docs/superpowers/specs/2026-05-19-cart-drawer-subscribe-and-upsell-design.md
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

function clearChildren(el) {
  if (!el) return;
  if (typeof el.replaceChildren === 'function') {
    el.replaceChildren();
  } else {
    while (el.firstChild) el.removeChild(el.firstChild);
  }
}

function createSpinnerSVG() {
  // Rotation is handled by CSS @keyframes cls-spin on .cls__spinner —
  // this is more reliable than SMIL animateTransform, which can silently
  // fail in certain Chromium contexts. The circle uses currentColor so it
  // inherits the correct stroke (dark on empty indicator, white on filled).
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'cls__spinner');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  svg.setAttribute('aria-hidden', 'true');
  const circle = document.createElementNS(SVG_NS, 'circle');
  circle.setAttribute('cx', '12');
  circle.setAttribute('cy', '12');
  circle.setAttribute('r', '9');
  circle.setAttribute('fill', 'none');
  circle.setAttribute('stroke', 'currentColor');
  circle.setAttribute('stroke-width', '2.6');
  // Circumference ≈ 56.5 (r=9); ~25% visible arc for a clean spinner look.
  circle.setAttribute('stroke-dasharray', '14 44');
  circle.setAttribute('stroke-linecap', 'round');
  svg.appendChild(circle);
  return svg;
}

class CartLineSubscribe extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.lineKey = this.dataset.lineKey;
    this.lineType = this.dataset.lineType;            // 'standard' | 'bulk' | 'bundle' | 'cartbulk' | 'wht'
    // Per-product packaging noun for subscribe labels ("3 bottles every 3 months").
    // Sourced from custom.bulk_unit (singular) in Liquid; defaults to 'bottle' when unset,
    // so the common case needs no metafield. Bundles ignore it (they read "of each").
    this.bulkUnit = ((this.dataset.bulkUnit || 'bottle').trim() || 'bottle').toLowerCase();

    this._isProcessing = false;
    this._externalMutationInFlight = false;
    this.picker = this.querySelector('.cls__picker');
    this.errorEl = this.querySelector('.cls__error');

    this._onGlobalMutationStart = () => { this._externalMutationInFlight = true; this.setAttribute('aria-busy', 'true'); };
    this._onGlobalMutationEnd = () => { this._externalMutationInFlight = false; this.removeAttribute('aria-busy'); };
    window.addEventListener('cart:mutation-start', this._onGlobalMutationStart);
    window.addEventListener('cart:mutation-end', this._onGlobalMutationEnd);

    // Bulk lines are their own category: a single unified control (Subscribe & Save toggle +
    // Buy 3 / Buy 6 tiers), never the standard frequency picker.
    if (this.lineType === 'cartbulk') {
      this.cbTiers = this.safeParseJSON(this.dataset.cb, []);
      this.cbMode = this.dataset.cbMode || 'onetime';
      this.cbTier = this.dataset.cbTier || (this.cbTiers[0] && this.cbTiers[0].tier) || '';
      this.maxPct = parseInt(this.dataset.maxPct, 10) || 0;
      this.renderCartBulkControl();
      return;
    }

    // WHT A/B treatment lines are their own category (gated by _bulk_test_variant in Liquid):
    // a Subscribe & Save toggle + 3 quantity/cadence tiers (q1/q2/q3). Mirrors cartbulk.
    if (this.lineType === 'wht') {
      this.whtTiers = this.safeParseJSON(this.dataset.wht, []);
      this.whtMode = this.dataset.whtMode || 'onetime';
      this.whtTier = this.dataset.whtTier || (this.whtTiers[1] && this.whtTiers[1].tier) || 'q2';
      this.whtSize = this.dataset.whtSize || '30ml';
      // Native tier-offer line (metafield-driven): apply writes the generic cartbulk prop
      // (_cart_bulk_tier) instead of the WHT magic token. Same control, different identity prop.
      this.whtNative = this.dataset.whtNative === 'true';
      this.maxPct = parseInt(this.dataset.maxPct, 10) || 0;
      this.renderWhtControl();
      return;
    }

    // Native protocol bundle lines (gated by _protocol_offer in Liquid): a 2-option
    // segmented control switching the WHOLE bundle one-time <-> monthly. Mirrors cartbulk/wht.
    if (this.lineType === 'protocol') {
      this.bundleId = this.dataset.bundleId || null;
      this.protocolMode = this.dataset.protocolMode === 'monthly' ? 'monthly' : 'onetime';
      this.protocolOtMoney = this.dataset.protocolOtMoney || '';
      this.protocolMoMoney = this.dataset.protocolMoMoney || '';
      this.renderProtocolControl();
      return;
    }

    this.bundleId = this.dataset.bundleId || null;
    this.currentPlanId = this.dataset.currentPlanId || null;
    this.currentQty = parseInt(this.dataset.currentQty, 10) || 1;
    this.maxPct = parseInt(this.dataset.maxPct, 10) || 0;
    this.plans = this.safeParseJSON(this.dataset.plans, []);
    this.priorState = this.dataset.priorState
      ? this.safeParseJSON(this.dataset.priorState, null)
      : null;
    this.pill = this.querySelector('.cls__pill');

    this.renderPicker();
    this.bindPillHandler();
    this.bindPlanRowHandlers();
  }

  disconnectedCallback() {
    // Abort any in-flight mutation fetches so they don't race against the next
    // mounted instance after fetchCartDrawer() re-renders the drawer.
    if (this._currentAbortController) {
      try { this._currentAbortController.abort(); } catch (_) {}
    }
    if (this._onGlobalMutationStart) window.removeEventListener('cart:mutation-start', this._onGlobalMutationStart);
    if (this._onGlobalMutationEnd) window.removeEventListener('cart:mutation-end', this._onGlobalMutationEnd);
  }

  // ── Bulk line control (line_type 'cartbulk') ────────────────────────────────
  // A unified control for a bulk line in EITHER mode: a Subscribe & Save toggle plus
  // Buy 3 / Buy 6 tiers. The toggle switches one-time <-> subscribe at the current tier;
  // the tiers switch 3 <-> 6 within the current mode. No standard frequency picker.

  /** Build the toggle + tier rows into the .cls__picker shell. */
  renderCartBulkControl() {
    if (!this.picker) return;
    const legend = this.picker.querySelector('legend');
    clearChildren(this.picker);
    if (legend) this.picker.appendChild(legend);
    if (!Array.isArray(this.cbTiers) || this.cbTiers.length === 0) return;

    // Subscribe & Save toggle — ONLY when subscribe bulk actually exists (both tiers have a
    // Cart Bulk plan). A bulk product without those plans is one-time only, so we show just
    // the tiers (no toggle, no misleading "up to 0%").
    const subAvailable = this.cbTiers.every((t) => t.has_sub) && this.maxPct > 0;
    if (subAvailable) {
      const header = document.createElement('div');
      header.className = 'cls__subscribed-header clb__header';
      const label = document.createElement('span');
      label.textContent = `Subscribe & Save up to ${this.maxPct}%`;
      header.appendChild(label);
      const sw = document.createElement('span');
      sw.className = 'cls__switch';
      sw.setAttribute('role', 'switch');
      sw.setAttribute('tabindex', '0');
      sw.setAttribute('aria-checked', this.cbMode === 'subscription' ? 'true' : 'false');
      const toggle = () => this.onCbToggle();
      sw.addEventListener('click', toggle);
      sw.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); } });
      header.appendChild(sw);
      this.picker.appendChild(header);
    }

    // Tier rows.
    for (const t of this.cbTiers) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'cls__plan-row clb__tier';
      row.dataset.tier = t.tier;
      row.setAttribute('role', 'radio');
      row.setAttribute('aria-checked', t.tier === this.cbTier ? 'true' : 'false');

      const indicator = document.createElement('span');
      indicator.className = 'cls__plan-indicator';
      row.appendChild(indicator);

      const name = document.createElement('span');
      name.className = 'cls__plan-name';
      name.textContent = this.cbMode === 'subscription'
        ? this.subscribeRowLabel(t.qty, `Every ${t.months} months`)
        : `Buy ${t.qty}`;
      row.appendChild(name);

      const price = document.createElement('span');
      price.className = 'clb__price';
      const finalSpan = document.createElement('span');
      finalSpan.className = 'clb__final';
      finalSpan.textContent = this.cbMode === 'subscription' ? t.sub_final_money : t.onetime_final_money;
      const regSpan = document.createElement('s');
      regSpan.className = 'clb__regular';
      regSpan.textContent = t.regular_money;
      price.appendChild(finalSpan);
      price.appendChild(document.createTextNode(' '));
      price.appendChild(regSpan);
      row.appendChild(price);

      const pct = document.createElement('span');
      pct.className = 'cls__plan-pct';
      pct.textContent = `${this.cbMode === 'subscription' ? t.sub_pct : t.onetime_pct}% OFF`;
      row.appendChild(pct);

      row.addEventListener('click', () => this.onCbTierClick(t.tier));
      this.picker.appendChild(row);
    }
  }

  onCbToggle() {
    if (this._externalMutationInFlight || this._isProcessing) return;
    const newMode = this.cbMode === 'subscription' ? 'onetime' : 'subscription';
    this.applyCartBulk(this.cbTier, newMode);
  }

  onCbTierClick(tier) {
    if (this._externalMutationInFlight || this._isProcessing) return;
    if (tier === this.cbTier) return; // already on this tier in the current mode
    this.applyCartBulk(tier, this.cbMode);
  }

  /**
   * Apply a bulk tier in a mode by updating the line in place.
   *  - subscription: switch to the tier's Cart Bulk plan at its qty (discount from Recharge).
   *  - onetime: set _cart_bulk_tier + qty, remove the plan (discount from the function).
   */
  async applyCartBulk(tier, mode) {
    if (this._externalMutationInFlight || this._isProcessing) return;
    const tierObj = (this.cbTiers || []).find((t) => t.tier === tier);
    if (!tierObj) return;
    if (mode === 'subscription' && !tierObj.has_sub) {
      this.showError('Subscription is not available for this option.');
      return;
    }

    this.applyCbOptimistic(tier, mode);
    try {
      const cart = await this.fetchCart();
      const ourLines = this.findOurLinesInCart(cart);
      if (ourLines.length === 0) throw new Error('Line not found in cart');
      const l = ourLines[0];

      const props = this.cloneVisibleProperties(l.properties);
      delete props._bundle_type;
      delete props._bundle_quantity;
      delete props._frontend_discount;
      delete props._original_price;
      delete props._hide_quantity;

      let payload;
      if (mode === 'subscription') {
        delete props._cart_bulk_tier;
        props._purchase_type = 'subscription';
        payload = { id: l.variant_id, quantity: tierObj.qty, selling_plan: tierObj.plan_id, properties: props };
      } else {
        props._purchase_type = 'onetime';
        props._cart_bulk_tier = tier;
        payload = { id: l.variant_id, quantity: tierObj.qty, properties: props };
      }
      await this.performMutation([payload], ourLines);
    } catch (err) {
      this.rollbackCbOptimistic();
      const msg = this.friendlyError(err);
      if (msg) this.showError(msg);
    }
  }

  applyCbOptimistic(tier, mode) {
    if (!this.picker) return;
    this.picker.setAttribute('aria-busy', 'true');
    const sw = this.picker.querySelector('.cls__switch');
    if (sw) sw.setAttribute('aria-checked', mode === 'subscription' ? 'true' : 'false');
    this.picker.querySelectorAll('.cls__plan-row').forEach((r) => {
      const active = r.dataset.tier === tier;
      r.setAttribute('aria-checked', active ? 'true' : 'false');
      const ind = r.querySelector('.cls__plan-indicator');
      if (ind) { clearChildren(ind); if (active) ind.appendChild(createSpinnerSVG()); }
    });
  }

  rollbackCbOptimistic() {
    if (!this.picker) return;
    this.picker.removeAttribute('aria-busy');
    const sw = this.picker.querySelector('.cls__switch');
    if (sw) sw.setAttribute('aria-checked', this.cbMode === 'subscription' ? 'true' : 'false');
    this.picker.querySelectorAll('.cls__plan-row').forEach((r) => {
      r.setAttribute('aria-checked', r.dataset.tier === this.cbTier ? 'true' : 'false');
      const ind = r.querySelector('.cls__plan-indicator');
      if (ind) clearChildren(ind);
    });
  }

  // ── WHT line control (line_type 'wht') ──────────────────────────────────────
  // A unified control for a WHT A/B treatment line in EITHER mode: a Subscribe & Save
  // toggle plus 3 quantity/cadence tiers (q1 = 1 btl/month, q2 = 2 btl/2mo, q3 = 3 btl/3mo).
  // The toggle switches one-time <-> subscribe at the current tier; the tiers switch
  // q1/q2/q3 within the current mode. Mirrors the cartbulk control. WHT-only: reached
  // solely when this.lineType === 'wht' (set only when _bulk_test_variant matches).

  /** Cadence phrase for the subscribe-row label: q1 => "month", q2/q3 => "2 months"/"3 months". */
  whtMonthsPhrase(months) {
    return Number(months) === 1 ? 'month' : `${months} months`;
  }

  /** Subscribe-mode tier name, verbatim with the PDP Delivery Schedule prop. */
  whtSubName(qty, months) {
    const q = Number(qty) || 1;
    const noun = q === 1 ? 'bottle' : 'bottles';
    return `${q} ${noun} delivered every ${this.whtMonthsPhrase(months)}`;
  }

  /** One-time-mode tier name, e.g. "2 Bottles" / "1 Bottle". */
  whtOneTimeName(qty) {
    const q = Number(qty) || 1;
    return `${q} ${q === 1 ? 'Bottle' : 'Bottles'}`;
  }

  /** Build the toggle + tier rows into the .cls__picker shell. Mirrors renderCartBulkControl. */
  renderWhtControl() {
    if (!this.picker) return;
    const legend = this.picker.querySelector('legend');
    clearChildren(this.picker);
    if (legend) this.picker.appendChild(legend);
    if (!Array.isArray(this.whtTiers) || this.whtTiers.length === 0) return;

    // Subscribe & Save toggle — WHT subscribe always exists (all 3 tiers carry a plan).
    const subAvailable = this.whtTiers.every((t) => t.has_sub) && this.maxPct > 0;
    if (subAvailable) {
      const header = document.createElement('div');
      header.className = 'cls__subscribed-header clb__header';
      const label = document.createElement('span');
      // WHT header is plain "Subscribe & Save" (no "up to N%") — the per-tier
      // badges carry the exact "Save $X" now, so the header % is redundant.
      // WHT-only: the cartbulk/standard headers keep their "up to N%".
      label.textContent = 'Subscribe & Save';
      header.appendChild(label);
      const sw = document.createElement('span');
      sw.className = 'cls__switch';
      sw.setAttribute('role', 'switch');
      sw.setAttribute('tabindex', '0');
      sw.setAttribute('aria-checked', this.whtMode === 'subscription' ? 'true' : 'false');
      const toggle = () => this.onWhtToggle();
      sw.addEventListener('click', toggle);
      sw.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); } });
      header.appendChild(sw);
      this.picker.appendChild(header);
    }

    // Tier rows.
    for (const t of this.whtTiers) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'cls__plan-row clb__tier';
      row.dataset.tier = t.tier;
      row.setAttribute('role', 'radio');
      row.setAttribute('aria-checked', t.tier === this.whtTier ? 'true' : 'false');

      const indicator = document.createElement('span');
      indicator.className = 'cls__plan-indicator';
      row.appendChild(indicator);

      const name = document.createElement('span');
      name.className = 'cls__plan-name';
      name.textContent = this.whtMode === 'subscription'
        ? this.whtSubName(t.qty, t.months)
        : this.whtOneTimeName(t.qty);
      row.appendChild(name);

      const price = document.createElement('span');
      price.className = 'clb__price';
      const finalSpan = document.createElement('span');
      finalSpan.className = 'clb__final';
      finalSpan.textContent = this.whtMode === 'subscription' ? t.sub_final_money : t.onetime_final_money;
      const regSpan = document.createElement('s');
      regSpan.className = 'clb__regular';
      regSpan.textContent = t.regular_money;
      price.appendChild(finalSpan);
      price.appendChild(document.createTextNode(' '));
      price.appendChild(regSpan);
      row.appendChild(price);

      // Savings badge: the dollar amount saved vs the regular (unit × qty) price,
      // not a percentage. Shown ONLY when there's an actual saving — one-time q1
      // (1 bottle, $0 off) renders NO badge; one-time q2/q3 and ALL subscribe tiers
      // show "Save $X". (Per-tier save $ comes from the Liquid wht_json.)
      const saveMoney = this.whtMode === 'subscription' ? t.sub_save_money : t.onetime_save_money;
      const savePct = this.whtMode === 'subscription' ? t.sub_pct : t.onetime_pct;
      if (Number(savePct) > 0) {
        const pct = document.createElement('span');
        pct.className = 'cls__plan-pct';
        pct.textContent = `Save ${saveMoney}`;
        row.appendChild(pct);
      }

      row.addEventListener('click', () => this.onWhtTierClick(t.tier));
      this.picker.appendChild(row);
    }
  }

  onWhtToggle() {
    if (this._externalMutationInFlight || this._isProcessing) return;
    const newMode = this.whtMode === 'subscription' ? 'onetime' : 'subscription';
    this.applyWht(this.whtTier, newMode);
  }

  onWhtTierClick(tier) {
    if (this._externalMutationInFlight || this._isProcessing) return;
    if (tier === this.whtTier) return; // already on this tier in the current mode
    this.applyWht(tier, this.whtMode);
  }

  /**
   * Apply a WHT tier in a mode by updating the line in place. Writes the SAME line
   * properties the PDP sets so the line's display + discount-function output stay correct.
   *  - subscription: switch to the tier's Recharge plan at its qty (1/2/3).
   *  - onetime: set the bottle-count props, remove the plan (discount from the function).
   * _bulk_test_variant is NEVER removed — the line must stay WHT-typed (qty hidden,
   * discount fn fires, this control keeps rendering).
   */
  async applyWht(tier, mode) {
    if (this._externalMutationInFlight || this._isProcessing) return;
    const tierObj = (this.whtTiers || []).find((t) => t.tier === tier);
    if (!tierObj) return;
    if (mode === 'subscription' && !tierObj.has_sub) {
      this.showError('Subscription is not available for this option.');
      return;
    }

    this.applyWhtOptimistic(tier, mode);
    try {
      const cart = await this.fetchCart();
      const ourLines = this.findOurLinesInCart(cart);
      if (ourLines.length === 0) throw new Error('Line not found in cart');
      const l = ourLines[0];

      const qty = tierObj.qty;
      const bundleType = qty === 1 ? '1_bottle' : qty === 2 ? '2_bottles' : '3_bottles';

      const props = this.cloneVisibleProperties(l.properties);
      // Persist the WHT identity + shared props the PDP writes.
      if (this.whtNative) {
        // Native tier-offer line: the generic cartbulk tier prop drives the discount fn
        // (q1/q2/q3 -> variant tier_onetime_pcts). No WHT token.
        props._cart_bulk_tier = tier;
        delete props._bulk_test_variant;
      } else {
        props._bulk_test_variant = 'wht_tier_test_2026';
      }
      props._size_variant = this.whtSize;
      props._bundle_quantity = String(qty);
      props._bundle_type = bundleType;
      props._hide_quantity = 'true';
      // Preserve the reusable checkout tier data across cart edits (the size's tier set is
      // unchanged; the checkout extension reads _tier_data for any tier-offer product).
      if (this.whtNative && l.properties && l.properties._tier_data) {
        props._tier_data = l.properties._tier_data;
      }

      let payload;
      if (mode === 'subscription') {
        // Mirror the PDP subscribe line: Delivery Schedule = bottle count + scaling cadence.
        const deliverySchedule = this.whtSubName(qty, tierObj.months);
        props._purchase_type = 'subscription';
        props['Delivery Schedule'] = deliverySchedule;
        props._delivery_frequency = deliverySchedule;
        delete props['Supply Duration'];
        payload = { id: l.variant_id, quantity: qty, selling_plan: tierObj.plan_id, properties: props };
      } else {
        // Mirror the PDP one-time line: Supply Duration = bottle count, no plan.
        props._purchase_type = 'onetime';
        props['Supply Duration'] = this.whtOneTimeName(qty);
        delete props['Delivery Schedule'];
        delete props._delivery_frequency;
        payload = { id: l.variant_id, quantity: qty, properties: props };
      }
      await this.performMutation([payload], ourLines);
    } catch (err) {
      this.rollbackWhtOptimistic();
      const msg = this.friendlyError(err);
      if (msg) this.showError(msg);
    }
  }

  applyWhtOptimistic(tier, mode) {
    if (!this.picker) return;
    this.picker.setAttribute('aria-busy', 'true');
    const sw = this.picker.querySelector('.cls__switch');
    if (sw) sw.setAttribute('aria-checked', mode === 'subscription' ? 'true' : 'false');
    this.picker.querySelectorAll('.cls__plan-row').forEach((r) => {
      const active = r.dataset.tier === tier;
      r.setAttribute('aria-checked', active ? 'true' : 'false');
      const ind = r.querySelector('.cls__plan-indicator');
      if (ind) { clearChildren(ind); if (active) ind.appendChild(createSpinnerSVG()); }
    });
  }

  rollbackWhtOptimistic() {
    if (!this.picker) return;
    this.picker.removeAttribute('aria-busy');
    const sw = this.picker.querySelector('.cls__switch');
    if (sw) sw.setAttribute('aria-checked', this.whtMode === 'subscription' ? 'true' : 'false');
    this.picker.querySelectorAll('.cls__plan-row').forEach((r) => {
      r.setAttribute('aria-checked', r.dataset.tier === this.whtTier ? 'true' : 'false');
      const ind = r.querySelector('.cls__plan-indicator');
      if (ind) clearChildren(ind);
    });
  }

  // ── Native protocol bundle control (line_type 'protocol') ───────────────────
  // A 2-option segmented control that switches the WHOLE protocol bundle between its
  // two PDP-offer modes, in place, by mutating every child line that shares _bundle_id:
  //   one-time : each child qty 3, NO selling plan  (90-day, ~$318.39 / 9 items)
  //   monthly  : each child qty 1, on its Protocol "Delivered every month" plan
  //              (read from the line's _protocol_monthly_plan prop) (~$99.88 / 3 items)
  // Both qty AND plan are set explicitly per child, and changes run sequentially with a
  // hard stop on the first failure, so a bundle can never end up half-switched.

  renderProtocolControl() {
    if (!this.picker) return;
    const legend = this.picker.querySelector('legend');
    clearChildren(this.picker);
    if (legend) this.picker.appendChild(legend);

    const options = [
      {
        mode: 'onetime',
        title: 'One-time',
        sub: '90-day supply',
        price: this.protocolOtMoney || '',
      },
      {
        mode: 'monthly',
        title: 'Monthly',
        sub: 'Subscribe & save',
        price: this.protocolMoMoney ? `${this.protocolMoMoney}/mo` : '',
      },
    ];

    const group = document.createElement('div');
    group.className = 'clp__segmented';
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', 'Protocol plan');

    for (const opt of options) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'clp__seg';
      btn.dataset.mode = opt.mode;
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', opt.mode === this.protocolMode ? 'true' : 'false');

      const title = document.createElement('span');
      title.className = 'clp__seg-title';
      title.textContent = opt.title;
      btn.appendChild(title);

      const sub = document.createElement('span');
      sub.className = 'clp__seg-sub';
      sub.textContent = opt.sub;
      btn.appendChild(sub);

      if (opt.price) {
        const price = document.createElement('span');
        price.className = 'clp__seg-price';
        price.textContent = opt.price;
        btn.appendChild(price);
      }

      btn.addEventListener('click', () => this.onProtocolSegClick(opt.mode));
      group.appendChild(btn);
    }

    this.picker.appendChild(group);
  }

  onProtocolSegClick(mode) {
    if (this._externalMutationInFlight || this._isProcessing) return;
    if (mode === this.protocolMode) return; // already on this mode
    this.applyProtocol(mode);
  }

  applyProtocolOptimistic(mode) {
    if (!this.picker) return;
    this.picker.setAttribute('aria-busy', 'true');
    this.picker.querySelectorAll('.clp__seg').forEach((b) => {
      b.setAttribute('aria-checked', b.dataset.mode === mode ? 'true' : 'false');
    });
  }

  rollbackProtocolOptimistic() {
    if (!this.picker) return;
    this.picker.removeAttribute('aria-busy');
    this.picker.querySelectorAll('.clp__seg').forEach((b) => {
      b.setAttribute('aria-checked', b.dataset.mode === this.protocolMode ? 'true' : 'false');
    });
  }

  /**
   * Switch the whole protocol bundle to `mode`. Gathers every line sharing this
   * _bundle_id and, per child, sets qty + selling_plan explicitly:
   *   monthly  -> qty 1, selling_plan = child._protocol_monthly_plan
   *   onetime  -> qty 3, selling_plan removed
   * Runs the /cart/change.js calls SEQUENTIALLY (performMutation awaits each in order)
   * and aborts the whole batch on the first failure, so the bundle is never left split
   * across modes. Properties are preserved verbatim except _purchase_type, which is
   * flipped to match the new mode (cart display + qty-lock gates read it).
   */
  async applyProtocol(mode) {
    if (this._externalMutationInFlight || this._isProcessing) return;

    this.applyProtocolOptimistic(mode);
    try {
      const cart = await this.fetchCart();
      const ourLines = this.findOurLinesInCart(cart);
      if (ourLines.length === 0) throw new Error('Line not found in cart');

      const targetQty = mode === 'monthly' ? 1 : 3;
      const payloads = [];
      for (const l of ourLines) {
        const props = this.cloneVisibleProperties(l.properties);
        const payload = { id: l.variant_id, quantity: targetQty, properties: props };
        if (mode === 'monthly') {
          const planId = props._protocol_monthly_plan;
          if (!planId) throw new Error('Missing _protocol_monthly_plan on a protocol line');
          payload.selling_plan = planId;     // subscribe each child to its Protocol plan
          props._purchase_type = 'subscription';
          props['Delivery Schedule'] = 'Delivery every month'; // mirror the PDP monthly add
        } else {
          // Omit selling_plan entirely -> performMutation sends null -> Shopify removes
          // the plan (the proven cartbulk one-time path does the same).
          props._purchase_type = 'onetime';
          delete props['Delivery Schedule'];                   // not a one-time line property
        }
        props._bundle_quantity = String(targetQty);
        props._bundle_type = mode === 'monthly' ? '1_bottles_each' : '3_bottles_each';
        payloads.push(payload);
      }

      await this.performMutation(payloads, ourLines);
    } catch (err) {
      this.rollbackProtocolOptimistic();
      const msg = this.friendlyError(err);
      if (msg) this.showError(msg);
    }
  }

  safeParseJSON(str, fallback) {
    if (!str) return fallback;
    try { return JSON.parse(str); }
    catch (e) { console.warn('[cart-line-subscribe] JSON parse failed', e); return fallback; }
  }

  /**
   * Build plan rows inside the existing <fieldset class="cls__picker">.
   * Marks the row with target_qty matching currentQty (clamped to max target_qty
   * in the picker) as auto-selected when no current plan is set.
   */
  renderPicker() {
    if (!this.picker) return;
    const legend = this.picker.querySelector('legend');
    clearChildren(this.picker);
    if (legend) this.picker.appendChild(legend);

    if (!Array.isArray(this.plans) || this.plans.length === 0) return;

    const maxTargetQty = Math.max(...this.plans.map(p => p.target_qty || 1));
    const autoSelectTargetQty = Math.min(this.currentQty, maxTargetQty);

    for (const plan of this.plans) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'cls__plan-row';
      row.dataset.planId = String(this.resolvePlanId(plan) || '');
      row.dataset.targetQty = String(plan.target_qty || 1);
      row.dataset.planPct = String(plan.pct || 0);
      row.setAttribute('role', 'radio');

      const isCurrent = this.currentPlanId &&
        String(this.resolvePlanId(plan)) === String(this.currentPlanId);

      const indicator = document.createElement('span');
      indicator.className = 'cls__plan-indicator';
      row.appendChild(indicator);

      const name = document.createElement('span');
      name.className = 'cls__plan-name';
      name.textContent = this.subscribeRowLabel(plan.target_qty, plan.label);
      row.appendChild(name);

      const pct = document.createElement('span');
      pct.className = 'cls__plan-pct';
      pct.textContent = `${Math.round(plan.pct || 0)}% OFF`;
      row.appendChild(pct);

      this.picker.appendChild(row);

      row.setAttribute('aria-checked', isCurrent ? 'true' : 'false');
      // Note: the auto-select highlight for state 1 → 2 happens at click time
      // via applyOptimisticSubscribing, not here at render time.
      void autoSelectTargetQty; // reserved for future render-time hint
    }
  }

  /**
   * For standard/bulk, plan_id sits on the plan object directly.
   * For bundle, plans hold a targets[] array. resolvePlanId returns the FIRST
   * target's plan_id as a stand-in for the data-plan-id attribute (used for
   * current-plan matching display only).
   */
  resolvePlanId(plan) {
    if (plan.plan_id) return plan.plan_id;
    if (Array.isArray(plan.targets) && plan.targets[0]) return plan.targets[0].plan_id;
    return '';
  }

  /**
   * Post-Liquid cleanup of plan labels — covers Recharge name variations the
   * Liquid replace filter chain might miss (case, multi-space, anchoring).
   * Mirrors bulk-v2.js:73-88.
   */
  cleanupPlanLabel(label) {
    return label
      .replace(/^Bundle:\s*delivery every\s+/i, 'Every ')
      .replace(/^Delivery every\s+/i, 'Every ')
      .replace(/^Delivered every\s+/i, 'Every ')
      .replace(/^Ships every\s+/i, 'Every ')
      .replace(/^Subscribe:\s*/i, '')
      .replace(/^Bundle:\s*/i, '');
  }

  /**
   * Build a subscribe-row label that states the quantity, not just the interval.
   * The frequency tiers silently scale quantity (1/2/3 bottles), so showing only
   * "Every N months" hides the bottle count and the price jump until checkout.
   * "Every 3 months" + qty 3 -> "3 bottles every 3 months". Bundles read "of each"
   * (the qty is per included product); the singular collapses to "1 bottle every month".
   */
  subscribeRowLabel(qty, rawLabel) {
    const q = Number(qty) || 1;
    const interval = this.cleanupPlanLabel(rawLabel || '').replace(/^Every\b/i, 'every');
    const unit = this.lineType === 'bundle'
      ? 'of each'
      : (q === 1 ? this.bulkUnit : this.pluralizeUnit(this.bulkUnit));
    return `${q} ${unit} ${interval}`.trim();
  }

  /**
   * Pluralize a packaging noun for counts > 1. Handles the regular case (+s) and the
   * common sibilant endings (+es: pouch->pouches, box->boxes). Packaging nouns ending in
   * -y are effectively nonexistent, so the -y->-ies rule is intentionally omitted. Mirrors
   * the Liquid pluralization in snippets/cart-bulk-offers.liquid (keep them in sync).
   */
  pluralizeUnit(unit) {
    const u = (unit || 'bottle');
    return /(?:s|x|z|ch|sh)$/i.test(u) ? u + 'es' : u + 's';
  }

  /**
   * Apply a subscription state change by updating each existing line IN PLACE via
   * /cart/change.js (keyed by 1-based line index). This preserves each line's
   * position in the cart — unlike remove+add, which moves the line to the top —
   * and one call sets quantity + selling_plan + properties together. Also restores
   * the cart-drawer scroll position across the fetchCartDrawer re-render.
   *
   * @param {Array} payloads - [{id (variant_id), quantity, selling_plan?, properties}, ...]
   * @param {Array} ourLines - the existing cart lines being changed (carry _line index)
   * @returns {Promise<void>} resolves after fetchCartDrawer() completes
   * @throws on failure (caller handles UI rollback)
   */
  async performMutation(payloads, ourLines) {
    if (this._isProcessing) throw new Error('Already processing');
    this._isProcessing = true;

    // Per-mutation AbortController so disconnectedCallback can cancel in-flight
    // fetches when the cart drawer re-renders during a mutation.
    const abortController = new AbortController();
    this._currentAbortController = abortController;

    window.cartIsMutating = true;
    window.dispatchEvent(new CustomEvent('cart:mutation-start'));

    // Preserve the cart-drawer scroll position across the fetchCartDrawer re-render.
    const getScroller = () => document.querySelector('cart-drawer-items');
    const savedScroll = getScroller() ? getScroller().scrollTop : 0;

    // Map each existing line's variant_id -> its 1-based cart line index.
    // /cart/change.js requires the `line` index (not the key) when changing
    // selling_plan, and updating in place preserves the line's position.
    const variantToLine = new Map();
    for (const l of ourLines) variantToLine.set(String(l.variant_id), l._line);

    try {
      // Update each line IN PLACE via /cart/change.js. Unlike remove+add this
      // keeps the line at its current position (no jump to the top of the cart)
      // and one call sets quantity + selling_plan + properties together.
      for (const payload of payloads) {
        const line = variantToLine.get(String(payload.id));
        if (!line) throw new Error('Line index not found for variant ' + payload.id);

        let properties = payload.properties || {};
        // change.js IGNORES an empty properties object (it won't clear stale
        // props). When removing the plan with nothing to restore, send the
        // subscribe-added keys as empty strings (falsy in Liquid) to clear them.
        if (Object.keys(properties).length === 0) {
          properties = { _bundle_type: '', _bundle_quantity: '', _purchase_type: '', _hide_quantity: '', _frontend_discount: '', _original_price: '' };
        }

        const resp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            line: line,
            quantity: payload.quantity,
            selling_plan: payload.selling_plan != null ? payload.selling_plan : null,
            properties: properties,
          }),
          signal: abortController.signal,
        });
        if (!resp.ok) {
          const txt = await resp.text().catch(() => '');
          throw new Error(`/cart/change.js failed (${resp.status}): ${txt.slice(0, 200)}`);
        }
      }

      // Trigger cart drawer re-render — destroys this component.
      // fetchCartDrawer() is fire-and-forget: it starts fetch().then(renderCartContents)
      // and returns undefined, so awaiting it does NOT wait for the DOM swap.
      // renderCartContents replaces #CartDrawer .drawer__inner's innerHTML, recreating
      // the scroll container (cart-drawer-items) at scrollTop 0. Schedule a restore
      // that fires when that swap actually lands.
      if (typeof window.fetchCartDrawer === 'function') {
        this.scheduleScrollRestore(savedScroll);
        window.fetchCartDrawer();
      } else {
        window.location.reload();
      }
    } finally {
      this._isProcessing = false;
      this._currentAbortController = null;
      window.cartIsMutating = false;
      window.dispatchEvent(new CustomEvent('cart:mutation-end'));
    }
  }

  /**
   * Restore the cart-drawer scroll position after the asynchronous, fire-and-forget
   * fetchCartDrawer() re-render. renderCartContents swaps #CartDrawer .drawer__inner's
   * innerHTML (recreating cart-drawer-items at 0); .drawer__inner itself persists, so
   * we watch it for the childList swap and restore scroll the moment it lands. The
   * observer is standalone (no `this`) so it outlives this component, which is
   * destroyed by the re-render.
   */
  scheduleScrollRestore(savedScroll) {
    if (!savedScroll || savedScroll <= 0 || !('MutationObserver' in window)) return;
    const inner = document.querySelector('#CartDrawer .drawer__inner');
    if (!inner) return;
    const restore = () => {
      const s = document.querySelector('cart-drawer-items');
      if (s) s.scrollTop = savedScroll;
    };
    const obs = new MutationObserver(() => {
      restore();
      requestAnimationFrame(restore);
    });
    obs.observe(inner, { childList: true });
    // Safety net: the swap lands within ~1s; stop observing after that.
    setTimeout(() => obs.disconnect(), 2500);
  }

  async fetchCart() {
    const signal = this._currentAbortController && this._currentAbortController.signal;
    const resp = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, signal });
    if (!resp.ok) throw new Error('Failed to fetch /cart.js');
    return resp.json();
  }

  bindPillHandler() {
    if (this.pill && !this.pill._bound) {
      this.pill._bound = true;
      this.pill.addEventListener('click', () => this.onPillClick());
      const sw = this.pill.querySelector('.cls__switch');
      if (sw) sw.addEventListener('click', (e) => { e.stopPropagation(); this.onPillClick(); });
    }

    // When already subscribed, no pill exists. Render a subscribed header
    // with an inline switch above the picker, so user can toggle off.
    if (this.currentPlanId && this.picker && !this.picker._subscribedHeaderRendered) {
      this.renderSubscribedHeader();
    }
  }

  renderSubscribedHeader() {
    if (!this.picker) return;
    this.picker._subscribedHeaderRendered = true;

    const header = document.createElement('div');
    header.className = 'cls__subscribed-header';
    // Styling lives entirely in cart-line-subscribe.css (.cls__subscribed-header);
    // no inline styles here so the stylesheet can own sizing without !important.

    const label = document.createElement('span');
    label.textContent = `Subscribe & Save up to ${this.maxPct}%`;
    header.appendChild(label);

    const sw = document.createElement('span');
    sw.className = 'cls__switch';
    sw.setAttribute('role', 'switch');
    sw.setAttribute('aria-checked', 'true');
    sw.setAttribute('tabindex', '0');
    sw.addEventListener('click', () => this.onUntoggleClick());
    header.appendChild(sw);

    // Insert after legend (which is the first child of the picker)
    const legend = this.picker.querySelector('legend');
    if (legend && legend.nextSibling) {
      this.picker.insertBefore(header, legend.nextSibling);
    } else {
      this.picker.appendChild(header);
    }
  }

  async onUntoggleClick() {
    if (this._externalMutationInFlight) return;
    if (this._isProcessing) return;
    this.applyOptimisticUntoggling();

    try {
      const cart = await this.fetchCart();
      const ourLines = this.findOurLinesInCart(cart);
      if (ourLines.length === 0) throw new Error('Line not found in cart');

      const firstLine = ourLines[0];
      const priorStateRaw = firstLine.properties && firstLine.properties._pre_sub_state;

      let addPayloads;
      if (priorStateRaw) {
        const prior = JSON.parse(priorStateRaw);
        addPayloads = this.buildRestorePayloads(prior);
      } else {
        addPayloads = this.buildSynthesizedOneTimePayloads(ourLines);
      }

      await this.performMutation(addPayloads, ourLines);
    } catch (err) {
      this.rollbackOptimisticUntoggle();
      const msg = this.friendlyError(err);
      if (msg) this.showError(msg);
    }
  }

  /**
   * Rebuild the original one-time line(s) from a stashed _pre_sub_state JSON.
   * Properties are restored verbatim except _pre_sub_state itself is dropped.
   */
  buildRestorePayloads(prior) {
    if (!prior) return [];
    if (prior.kind === 'bundle') {
      const newBundleId = 'bundle_' + Date.now();
      return (prior.children || []).map(child => {
        const props = Object.assign({}, child.properties);
        delete props._pre_sub_state;
        props._bundle_id = newBundleId;
        return {
          id: child.variant_id,
          quantity: child.quantity,
          selling_plan: child.selling_plan_id || undefined,
          properties: props,
        };
      });
    }
    const props = Object.assign({}, prior.properties || {});
    delete props._pre_sub_state;
    return [{
      id: prior.variant_id,
      quantity: prior.quantity,
      selling_plan: prior.selling_plan_id || undefined,
      properties: props,
    }];
  }

  /**
   * No prior state — user added this as a subscription from the PDP.
   * Synthesize the one-time equivalent at the same qty, matching the property
   * shape that bulk-v2.js / bundle-variants.js produce on PDP one-time add.
   */
  buildSynthesizedOneTimePayloads(ourLines) {
    if (this.lineType === 'standard') {
      const l = ourLines[0];
      const props = this.cloneVisibleProperties(l.properties);
      delete props._purchase_type;
      delete props._hide_quantity;
      return [{
        id: l.variant_id,
        quantity: l.quantity,
        properties: props,
      }];
    }

    if (this.lineType === 'bulk') {
      const l = ourLines[0];
      const qty = parseInt(l.quantity, 10) || 1;
      const clampedQty = Math.min(qty, 3);
      const bundleType = clampedQty === 1 ? '1_bottle' : clampedQty === 2 ? '2_bottles' : '3_bottles';
      const frontendDiscount = clampedQty === 1 ? '0%' : clampedQty === 2 ? '5%' : '10%';

      const props = this.cloneVisibleProperties(l.properties);
      delete props._hide_quantity;
      props._bundle_type = bundleType;
      props._bundle_quantity = String(clampedQty);
      props._purchase_type = 'onetime';
      props._frontend_discount = frontendDiscount;
      // _original_price is the un-discounted TOTAL price in cents (matches the
      // shape bulk-v2.js:~789 emits: basePrice * quantity). l.original_price is
      // per-unit pre-discount on the cart line; multiply by qty for the total.
      const undiscountedUnit = l.original_price || l.price || 0;
      props._original_price = String(undiscountedUnit * qty);

      return [{
        id: l.variant_id,
        quantity: qty,
        properties: props,
      }];
    }

    if (this.lineType === 'bundle') {
      const newBundleId = 'bundle_' + Date.now();
      const qty = this.currentQty;
      const bundleType = qty === 1 ? '1_bottles_each' : qty === 2 ? '2_bottles_each' : '3_bottles_each';

      return ourLines.map((l, i) => {
        const props = this.cloneVisibleProperties(l.properties);
        delete props._hide_quantity;
        props._bundle_id = newBundleId;
        props._bundle_type = bundleType;
        props._bundle_quantity = String(qty);
        props._purchase_type = 'onetime';

        if (i > 0) {
          for (const k of Object.keys(props)) {
            if (k.indexOf('_eguide_') === 0) delete props[k];
          }
        }

        return {
          id: l.variant_id,
          quantity: qty,
          properties: props,
        };
      });
    }

    throw new Error('Unknown line_type for synthesis: ' + this.lineType);
  }

  applyOptimisticUntoggling() {
    // Flip the toggle off and dim the picker — no spinner inside the switch
    // (a spinner overlapping the toggle knob looks broken). The toggle slide
    // off + dimmed picker is the feedback.
    if (this.picker) this.picker.setAttribute('aria-busy', 'true');
    const sw = this.querySelector('.cls__subscribed-header .cls__switch');
    if (sw) sw.setAttribute('aria-checked', 'false');
  }

  rollbackOptimisticUntoggle() {
    if (this.picker) this.picker.removeAttribute('aria-busy');
    const sw = this.querySelector('.cls__subscribed-header .cls__switch');
    if (sw) sw.setAttribute('aria-checked', 'true');
  }

  bindPlanRowHandlers() {
    if (!this.picker) return;
    this.picker.addEventListener('click', async (e) => {
      const row = e.target.closest('.cls__plan-row');
      if (!row || row.getAttribute('aria-disabled') === 'true') return;
      if (this._externalMutationInFlight) return;
      if (this._isProcessing) return;

      const planTargetQty = Number(row.dataset.targetQty);
      const chosenPlan = this.plans.find(p => p.target_qty === planTargetQty);
      if (!chosenPlan) return;

      if (this.currentPlanId) {
        const currentlyChecked = this.picker.querySelector('.cls__plan-row[aria-checked="true"]');
        if (currentlyChecked === row) return;
        await this.swapPlan(chosenPlan, row);
      } else {
        await this.applySubscription(chosenPlan);
      }
    });
  }

  async swapPlan(chosenPlan, rowEl) {
    if (this._externalMutationInFlight) return;
    if (this._isProcessing) return;
    this.applyOptimisticSwap(rowEl);

    try {
      const cart = await this.fetchCart();
      const ourLines = this.findOurLinesInCart(cart);
      if (ourLines.length === 0) throw new Error('Line not found in cart');

      const firstLine = ourLines[0];
      const carryPriorState = (firstLine.properties && firstLine.properties._pre_sub_state) || null;

      const addPayloads = this.buildSubscribeAddPayloads(chosenPlan, ourLines, carryPriorState);
      await this.performMutation(addPayloads, ourLines);
    } catch (err) {
      this.rollbackOptimisticUI();
      const msg = this.friendlyError(err);
      if (msg) this.showError(msg);
    }
  }

  applyOptimisticSwap(newRow) {
    if (!this.picker) return;
    this.picker.setAttribute('aria-busy', 'true');
    this.picker.querySelectorAll('.cls__plan-row').forEach(r => {
      r.setAttribute('aria-checked', 'false');
      const ind = r.querySelector('.cls__plan-indicator');
      if (ind) clearChildren(ind);
    });
    newRow.setAttribute('aria-checked', 'true');
    const ind = newRow.querySelector('.cls__plan-indicator');
    if (ind) {
      clearChildren(ind);
      ind.appendChild(createSpinnerSVG());
    }
  }

  async onPillClick() {
    if (this._externalMutationInFlight) return;
    if (this._isProcessing) return;
    if (this.currentPlanId) return;
    if (!this.plans || this.plans.length === 0) return;

    const maxTargetQty = Math.max(...this.plans.map(p => p.target_qty || 1));
    const targetQty = Math.min(this.currentQty, maxTargetQty);
    const chosen = this.plans.find(p => p.target_qty === targetQty) || this.plans[0];
    await this.applySubscription(chosen);
  }

  async applySubscription(chosenPlan) {
    if (this._externalMutationInFlight) return;
    if (this._isProcessing) return;
    this.applyOptimisticSubscribing(chosenPlan);

    try {
      const cart = await this.fetchCart();
      const ourLines = this.findOurLinesInCart(cart);
      if (ourLines.length === 0) throw new Error('Line not found in cart');

      const priorState = this.capturePriorState(ourLines);
      const priorStateJSON = JSON.stringify(priorState);

      const addPayloads = this.buildSubscribeAddPayloads(chosenPlan, ourLines, priorStateJSON);
      await this.performMutation(addPayloads, ourLines);
    } catch (err) {
      this.rollbackOptimisticUI();
      const msg = this.friendlyError(err);
      if (msg) this.showError(msg);
    }
  }

  findOurLinesInCart(cart) {
    let result;
    if ((this.lineType === 'bundle' || this.lineType === 'protocol') && this.bundleId) {
      result = cart.items.filter(it => it.properties && it.properties._bundle_id === this.bundleId);
    } else {
      const byKey = cart.items.find(it => it.key === this.lineKey);
      result = byKey ? [byKey] : [];
    }
    // Attach the 1-based cart line index — required by /cart/change.js (selling_plan
    // changes must target a `line` index, and in-place updates preserve position).
    for (const l of result) l._line = cart.items.indexOf(l) + 1;
    return result;
  }

  capturePriorState(ourLines) {
    if (this.lineType === 'bundle') {
      return {
        schema_version: 1,
        kind: 'bundle',
        bundle_id: this.bundleId,
        children: ourLines.map(l => ({
          variant_id: l.variant_id,
          quantity: l.quantity,
          selling_plan_id: l.selling_plan_allocation ? l.selling_plan_allocation.selling_plan.id : null,
          properties: this.cloneVisibleProperties(l.properties),
        })),
      };
    }
    const l = ourLines[0];
    return {
      schema_version: 1,
      kind: 'single',
      variant_id: l.variant_id,
      quantity: l.quantity,
      selling_plan_id: l.selling_plan_allocation ? l.selling_plan_allocation.selling_plan.id : null,
      properties: this.cloneVisibleProperties(l.properties),
    };
  }

  cloneVisibleProperties(props) {
    if (!props) return {};
    const out = {};
    for (const k of Object.keys(props)) {
      if (k === '_pre_sub_state') continue;
      out[k] = props[k];
    }
    return out;
  }

  buildSubscribeAddPayloads(chosenPlan, ourLines, priorStateJSON) {
    switch (this.lineType) {
      case 'standard':
        return [this.buildStandardSubscribePayload(chosenPlan, ourLines[0], priorStateJSON)];
      case 'bulk':
        return [this.buildBulkSubscribePayload(chosenPlan, ourLines[0], priorStateJSON)];
      case 'bundle':
        return this.buildBundleSubscribePayloads(chosenPlan, ourLines, priorStateJSON);
      default:
        throw new Error('Unknown line_type: ' + this.lineType);
    }
  }

  buildStandardSubscribePayload(chosenPlan, line, priorStateJSON) {
    const properties = this.cloneVisibleProperties(line.properties);
    properties._pre_sub_state = priorStateJSON;
    return {
      id: line.variant_id,
      quantity: line.quantity,
      selling_plan: chosenPlan.plan_id,
      properties,
    };
  }

  /**
   * Build a bulk subscription payload at the chosenPlan's target_qty.
   *
   * Bulk synthesis matches the alt-pdp-buy-buttons-v2 / bulk-v2 line-item
   * property structure on the subscription path. See:
   *   - alt-pdp-buy-buttons-v2.liquid:407-428
   *   - bulk-v2.js:403-406
   */
  buildBulkSubscribePayload(chosenPlan, line, priorStateJSON) {
    const targetQty = chosenPlan.target_qty;
    const bundleType = targetQty === 1 ? '1_bottle'
                     : targetQty === 2 ? '2_bottles'
                     : '3_bottles';

    const properties = this.cloneVisibleProperties(line.properties);
    delete properties._frontend_discount;
    delete properties._original_price;
    properties._bundle_type = bundleType;
    properties._bundle_quantity = String(targetQty);
    properties._purchase_type = 'subscription';
    properties._hide_quantity = 'true';
    properties._pre_sub_state = priorStateJSON;

    return {
      id: line.variant_id,
      quantity: targetQty,
      selling_plan: chosenPlan.plan_id,
      properties,
    };
  }

  /**
   * Build bundle subscription payloads — one per included product.
   *
   * Mirrors bundle-variants.js subscription path: each child gets the per-product
   * selling_plan at [target_qty + 2], quantity = target_qty per child, shared
   * new _bundle_id. _pre_sub_state is stashed on the FIRST child only (matches
   * the bundle e-guide convention).
   */
  buildBundleSubscribePayloads(chosenPlan, ourLines, priorStateJSON) {
    if (!Array.isArray(chosenPlan.targets) || chosenPlan.targets.length === 0) {
      throw new Error('Bundle plan missing targets[]');
    }

    const newBundleId = 'bundle_' + Date.now();
    const targetQty = chosenPlan.target_qty;
    const bundleType = targetQty + '_bottles_each';

    const linesByVariantId = new Map();
    for (const l of ourLines) linesByVariantId.set(String(l.variant_id), l);

    const payloads = [];
    for (let i = 0; i < chosenPlan.targets.length; i++) {
      const target = chosenPlan.targets[i];
      const existingLine = linesByVariantId.get(String(target.variant_id));
      const properties = existingLine
        ? this.cloneVisibleProperties(existingLine.properties)
        : {};

      delete properties._frontend_discount;
      delete properties._original_price;

      properties._bundle_id = newBundleId;
      properties._bundle_type = bundleType;
      properties._bundle_quantity = String(targetQty);
      properties._purchase_type = 'subscription';
      properties._hide_quantity = 'true';

      if (i === 0) {
        properties._pre_sub_state = priorStateJSON;
      }

      // E-guide properties survive on the first item only (existing bundle rule)
      if (i > 0) {
        for (const k of Object.keys(properties)) {
          if (k.indexOf('_eguide_') === 0) delete properties[k];
        }
      }

      payloads.push({
        id: target.variant_id,
        quantity: targetQty,
        selling_plan: target.plan_id,
        properties,
      });
    }

    return payloads;
  }

  /**
   * Optimistic UI for state 1 → 2:
   *  - Switch ON visual; reveal picker
   *  - Mark chosenPlan row's indicator as spinner
   */
  applyOptimisticSubscribing(chosenPlan) {
    // Hide the pill while the picker is open so the two states don't stack.
    if (this.pill) this.pill.setAttribute('hidden', '');
    const sw = this.pill && this.pill.querySelector('.cls__switch');
    if (sw) sw.setAttribute('aria-checked', 'true');
    if (this.picker) {
      this.picker.removeAttribute('hidden');
      this.picker.setAttribute('aria-busy', 'true');
    }
    const targetRow = this.picker
      ? [...this.picker.querySelectorAll('.cls__plan-row')]
          .find(r => Number(r.dataset.targetQty) === chosenPlan.target_qty)
      : null;
    if (targetRow) {
      targetRow.setAttribute('aria-checked', 'true');
      const ind = targetRow.querySelector('.cls__plan-indicator');
      if (ind) {
        clearChildren(ind);
        ind.appendChild(createSpinnerSVG());
      }
    }
  }

  rollbackOptimisticUI() {
    // Restore the pill (hidden during the optimistic subscribe attempt).
    if (this.pill) this.pill.removeAttribute('hidden');
    const sw = this.pill && this.pill.querySelector('.cls__switch');
    if (sw) sw.setAttribute('aria-checked', 'false');
    if (this.picker) {
      this.picker.setAttribute('hidden', '');
      this.picker.removeAttribute('aria-busy');
      this.picker.querySelectorAll('.cls__plan-row').forEach(r => {
        r.setAttribute('aria-checked', 'false');
        const ind = r.querySelector('.cls__plan-indicator');
        if (ind) clearChildren(ind);
      });
    }
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg || 'Something went wrong. Please try again.';
    this.errorEl.removeAttribute('hidden');
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.setAttribute('hidden', '');
    this.errorEl.textContent = '';
  }

  friendlyError(err) {
    if (err && err.name === 'AbortError') return null;  // suppressed
    if (err && err.message && err.message.includes('not found in cart')) {
      return 'Cart was updated elsewhere. Refresh and try again.';
    }
    return 'Could not update subscription. Please try again.';
  }
}

if (!customElements.get('cart-line-subscribe')) {
  customElements.define('cart-line-subscribe', CartLineSubscribe);
}
